// TUT-291b — o aviso de "movido, Desfazer" extraído do `KanbanPage.tsx`
// (onde nasceu como `KanbanToast`, TUT-265) para o quadro simples usar o
// MESMO componente. É a peça mais segura de compartilhar: não sabe nada de
// processo, peça, laudo ou perícia — só de mensagem, tom e um `onUndo`
// genérico —, e por isso sai inteira sem risco para quem já a usava.
import { Check, CircleAlert, Undo2, UserRoundCog, X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface QuadroToastState {
  id: number;
  message: string;
  tone: "success" | "critical";
  /** Ausente quando o movimento não é revertível. */
  onUndo: (() => void) | null;
  /**
   * TUT-524 (D18) — o gesto seguinte, no próprio aviso: o "eu pego" oferece
   * "Trocar responsável". Mesmo desenho do "Desfazer".
   */
  acao?: { rotulo: string; aoClicar: () => void; icone?: LucideIcon } | undefined;
}

const botaoDoAviso =
  "inline-flex items-center gap-[0.35rem] shrink-0 grow-0 px-[0.65rem] py-[0.35rem] text-corpo font-semibold text-primary bg-[var(--primary-highlight)] border border-primary rounded-[7px] cursor-pointer";

export function QuadroToast({
  toast,
  onDismiss,
}: {
  toast: QuadroToastState;
  onDismiss: () => void;
}) {
  return (
    <output
      className={cn(
        "fixed left-1/2 bottom-[1.6rem] max-[980px]:bottom-[calc(5.25rem+env(safe-area-inset-bottom))] z-[80] flex items-center gap-[0.7rem] max-w-[min(92vw,34rem)] py-[0.7rem] pr-[0.75rem] pl-[0.95rem] text-foreground bg-surface border border-border-strong rounded-[10px] shadow-[0_16px_40px_rgba(0,0,0,0.35)] -translate-x-1/2 animate-[kanban-toast-in_var(--dur-slow)_ease-out] motion-reduce:animate-none",
        toast.tone === "critical" &&
          "border-critical bg-[color-mix(in_srgb,var(--critical-highlight)_45%,var(--surface))]",
      )}
      role={toast.tone === "critical" ? "alert" : "status"}
      aria-live={toast.tone === "critical" ? "assertive" : "polite"}
    >
      {toast.tone === "critical" ? (
        <CircleAlert
          className="shrink-0 size-[1.1rem] text-critical"
          aria-hidden="true"
        />
      ) : (
        <Check
          className="shrink-0 size-[1.1rem] text-[var(--success)]"
          aria-hidden="true"
        />
      )}
      {/* TUT-524 (D18) — a recusa diz o motivo INTEIRO: o aviso crítico quebra
          a linha. Numa linha só, com reticências, "Ninguém pegou este
          trabalho: devolva para Aguardando elaboração e pegue de lá" saía
          cortada no celular, e um motivo cortado não diz nada. O de sucesso
          segue numa linha, ao lado do "Desfazer". Guardas:
          `QuadroToast.test.tsx` e a e2e `elaboracao-kanban.spec.ts`. */}
      <span
        className={cn(
          "flex-1 min-w-0 text-corpo",
          toast.tone === "critical" ? "break-words" : "overflow-hidden text-ellipsis whitespace-nowrap",
        )}
      >
        {toast.message}
      </span>
      {toast.onUndo ? (
        <button
          type="button"
          className={botaoDoAviso}
          onClick={() => toast.onUndo?.()}
        >
          <Undo2 aria-hidden="true" className="size-[0.95rem]" />
          Desfazer
        </button>
      ) : null}
      {toast.acao ? (
        <button type="button" className={botaoDoAviso} onClick={() => toast.acao?.aoClicar()}>
          {/* TUT-585 — o "Ver" da conta criada tem ícone próprio; o padrão segue o "Trocar responsável". */}
          {toast.acao.icone ? (
            <toast.acao.icone aria-hidden="true" className="size-[0.95rem]" />
          ) : (
            <UserRoundCog aria-hidden="true" className="size-[0.95rem]" />
          )}
          {toast.acao.rotulo}
        </button>
      ) : null}
      <button
        type="button"
        className="inline-flex shrink-0 grow-0 p-[0.25rem] text-[var(--muted)] bg-transparent border-0 rounded-[6px] cursor-pointer hover:text-foreground"
        onClick={onDismiss}
        aria-label="Dispensar aviso"
      >
        <X aria-hidden="true" className="size-[1rem]" />
      </button>
    </output>
  );
}
